/**
 * Tracked Programs Helper Functions
 * Handles CRUD operations for tracked_programs JSONB column in profiles table
 * 
 * This module provides a clean API for managing user programs using JSONB storage
 * instead of a separate user_programs table.
 */

import { supabase, handleSupabaseError } from '@/lib/supabase';
import type { TrackedProgram, NewTrackedProgram } from '@/types/database';

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Validates that a program object has required fields
 * @private
 */
function validateProgram(program: Partial<TrackedProgram>): boolean {
  // Very permissive validation - just require that we have some data
  // This matches the old behavior where any data was accepted
  const hasData = program && Object.keys(program).length > 0;
  
  console.log('Validation check:', {
    program,
    hasData,
    result: hasData
  });
  
  return hasData;
}

/**
 * Generates a new UUID for a program
 * @private
 */
function generateProgramId(): string {
  return crypto.randomUUID();
}

/**
 * Creates a timestamp string for the current moment
 * @private
 */
function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

// ============================================
// CORE CRUD OPERATIONS
// ============================================

/**
 * Retrieves all tracked programs for a user from their profile
 * 
 * @param {string} userId - The UUID of the user
 * @returns {Promise<TrackedProgram[]>} Array of tracked programs
 * @throws {Error} If database query fails
 * 
 * @example
 * const programs = await getTrackedPrograms('user-uuid-here');
 * console.log(`Found ${programs.length} programs`);
 */
/**
 * Normalize date strings to YYYY-MM-DD format
 * Extracts date part directly to avoid timezone conversion issues
 */
function normalizeDateString(dateValue: any, addDayCompensation: boolean = false): string {
  if (!dateValue) return '';
  
  // If it's already a clean YYYY-MM-DD string, return as-is (no timezone conversion needed)
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim())) {
    return dateValue.trim();
  }
  
  try {
    // Extract YYYY-MM-DD from ISO strings or other date formats
    // Match YYYY-MM-DD pattern at the start of the string (handles ISO format like "2024-06-15T00:00:00Z")
    const dateMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      // Validate date components
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      
      // Validate the date is reasonable
      if (yearNum >= 1900 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
        // Return the date string directly without any Date object conversion
        // This avoids timezone shifts entirely
        return `${year}-${month}-${day}`;
      }
    }
    
    // If it's a Date object, use UTC methods to avoid timezone shifts
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return '';
      // Use UTC methods to get the date components without timezone conversion
      const year = dateValue.getUTCFullYear();
      const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Try parsing as Date and use UTC methods
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch {
    return '';
  }
  
  return '';
}

/**
 * Recursively sanitize all date fields in program_data
 * @param programData - The program data to sanitize
 * @param addDayCompensation - Deprecated: No longer used. Dates are extracted directly without compensation.
 */
function sanitizeProgramDataDates(programData: any, addDayCompensation: boolean = false): any {
  if (!programData || typeof programData !== 'object') return programData;
  
  const dateFields = [
    'camp_start_date', 'camp_end_date', 'start_date', 'end_date',
    'registration_open_date', 'early_registration_open_date',
    'registration_deadline', 'early_bird_deadline', 'cancellation_deadline',
    'campStartDate', 'campEndDate', 'registrationOpenDate', 'earlyRegistrationOpenDate',
    'registrationDeadline', 'earlyBirdDeadline', 'cancellationDeadline'
  ];
  
  const sanitized = Array.isArray(programData) ? [...programData] : { ...programData };
  
  if (Array.isArray(sanitized)) {
    return sanitized.map(item => sanitizeProgramDataDates(item, addDayCompensation));
  }
  
  dateFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = normalizeDateString(sanitized[field], addDayCompensation);
    }
  });
  
  // Recursively sanitize nested objects
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] && typeof sanitized[key] === 'object' && !dateFields.includes(key)) {
      sanitized[key] = sanitizeProgramDataDates(sanitized[key], addDayCompensation);
    }
  });
  
  return sanitized;
}

export async function getTrackedPrograms(userId: string): Promise<TrackedProgram[]> {
  try {
    // 1. Fetch current data
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('tracked_programs')
      .eq('id', userId)
      .maybeSingle(); // Use maybeSingle instead of single

    // Handle case where profile doesn't exist yet
    if (fetchError) {
      // PGRST116 means no rows found - this is OK for new users
      if (fetchError.code === 'PGRST116') {
        console.log('Profile not found for user, returning empty array');
        return [];
      }
      console.error('Error in getTrackedPrograms:', fetchError);
      throw new Error(`Failed to fetch tracked programs: ${fetchError.message}`);
    }

    // 2. Get programs array
    const programs = Array.isArray(profile?.tracked_programs) ? profile.tracked_programs : [];
    
    // 3. Sanitize all dates in program_data to prevent timezone issues
    // Add a day compensation when reading from database to fix timezone shift
    const sanitizedPrograms = programs.map(program => ({
      ...program,
      program_data: sanitizeProgramDataDates(program.program_data, true) // true = add day compensation
    }));
    
    return sanitizedPrograms;
    
  } catch (error) {
    console.error('Error in getTrackedPrograms:', error);
    throw error;
  }
}

/**
 * Get programs filtered by status
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} status - Program status to filter by
 * @returns {Promise<TrackedProgram[]>} Filtered programs
 */
export async function getTrackedProgramsByStatus(
  userId: string, 
  status: string
): Promise<TrackedProgram[]> {
  try {
    const programs = await getTrackedPrograms(userId);
    
    console.log(`getTrackedProgramsByStatus - Looking for status "${status}" in ${programs.length} programs:`, 
      programs.map(p => ({
        name: p.program_data?.name,
        status: p.status,
        selectedStatuses: p.program_data?.selected_statuses
      }))
    );
    
    const filteredPrograms = programs.filter(program => {
      // Check both old format (single status) and new format (array of statuses)
      const hasStatus = program.status === status || 
                       (Array.isArray(program.status) && program.status.includes(status)) ||
                       (program.program_data?.selected_statuses && 
                        Array.isArray(program.program_data.selected_statuses) && 
                        program.program_data.selected_statuses.includes(status));
      
      // Debug logging for booked status
      if (status === 'booked') {
        console.log(`getTrackedProgramsByStatus - Checking ${program.program_data?.name}:`, {
          programStatus: program.status,
          selectedStatuses: program.program_data?.selected_statuses,
          hasStatus,
          status
        });
      }
      
      return hasStatus;
    });
    
    console.log(`getTrackedProgramsByStatus - Found ${filteredPrograms.length} programs with status "${status}"`);
    
    return filteredPrograms;
  } catch (error) {
    console.error('Error in getTrackedProgramsByStatus:', error);
    throw error;
  }
}

/**
 * Get programs for a specific year
 * 
 * @param {string} userId - The UUID of the user
 * @param {number} year - Year to filter by
 * @returns {Promise<TrackedProgram[]>} Programs for specified year
 */
export async function getTrackedProgramsByYear(
  userId: string, 
  year: number
): Promise<TrackedProgram[]> {
  try {
    const programs = await getTrackedPrograms(userId);
    return programs.filter(program => program.year_tracked === year);
  } catch (error) {
    console.error('Error in getTrackedProgramsByYear:', error);
    throw error;
  }
}

/**
 * Get a single program by ID
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} programId - The UUID of the program
 * @returns {Promise<TrackedProgram | null>} The program or null if not found
 */
export async function getTrackedProgramById(
  userId: string, 
  programId: string
): Promise<TrackedProgram | null> {
  try {
    const programs = await getTrackedPrograms(userId);
    return programs.find(program => program.id === programId) || null;
  } catch (error) {
    console.error('Error in getTrackedProgramById:', error);
    throw error;
  }
}

/**
 * Add a new tracked program to user's profile
 * 
 * @param {string} userId - The UUID of the user
 * @param {NewTrackedProgram} program - Program data to add
 * @returns {Promise<TrackedProgram>} The created program with generated ID
 * @throws {Error} If validation fails or database update fails
 */
export async function addTrackedProgram(
  userId: string, 
  program: NewTrackedProgram
): Promise<TrackedProgram> {
  try {
    // Debug: Log what we're receiving
    console.log('addTrackedProgram received:', JSON.stringify(program, null, 2));
    
    // 1. Validate
    if (!validateProgram(program)) {
      console.error('Validation failed for program:', program);
      throw new Error('Invalid program data: missing required fields');
    }

    // 2. Fetch current programs
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('tracked_programs')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    // 3. Prepare new program with defaults (matching old database schema)
    // Handle case where profile doesn't exist yet
    const currentPrograms = Array.isArray(profile?.tracked_programs) 
      ? profile.tracked_programs 
      : [];

    // Sanitize dates in program_data before creating
    // Don't add day compensation when saving - we want to save the user's selected date
    const sanitizedProgramData = program.program_data ? sanitizeProgramDataDates(program.program_data, false) : {};
    
    const newProgram: TrackedProgram = {
      id: generateProgramId(),
      program_type: program.program_type || 'camp', // Default from old schema
      status: program.status || 'considering', // Default from old schema
      year_tracked: program.year_tracked || new Date().getFullYear(), // Default to current year
      child_names: Array.isArray(program.child_names) ? program.child_names : [], // Default to empty array
      program_data: sanitizedProgramData,
      created_at: getCurrentTimestamp(),
      updated_at: getCurrentTimestamp()
    };

    // 4. Update database - create profile if it doesn't exist
    if (!profile) {
      // Profile doesn't exist, get user email from auth and create profile
      const { data: { user } } = await supabase.auth.getUser();
      const { error: createError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: user?.email || null,
          tracked_programs: [newProgram],
          updated_at: getCurrentTimestamp()
        });

      if (createError) {
        console.error('Error creating profile:', createError);
        throw new Error(`Failed to create profile: ${createError.message}`);
      }
    } else {
      // Profile exists, update it
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          tracked_programs: [...currentPrograms, newProgram],
          updated_at: getCurrentTimestamp()
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        throw new Error(`Failed to add tracked program: ${updateError.message}`);
      }
    }

    return newProgram;
    
  } catch (error) {
    console.error('Error in addTrackedProgram:', error);
    throw error;
  }
}

/**
 * Update an existing tracked program
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} programId - The UUID of the program to update
 * @param {Partial<TrackedProgram>} updates - Fields to update
 * @returns {Promise<TrackedProgram>} The updated program
 * @throws {Error} If program not found or update fails
 */
export async function updateTrackedProgram(
  userId: string, 
  programId: string, 
  updates: Partial<TrackedProgram>
): Promise<TrackedProgram> {
  try {
    // 1. Fetch current programs
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('tracked_programs')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    // Handle case where profile doesn't exist yet
    if (!profile) {
      throw new Error(`Profile not found for user ${userId}`);
    }

    const currentPrograms = Array.isArray(profile?.tracked_programs) 
      ? profile.tracked_programs 
      : [];

    // 2. Find program index
    const programIndex = currentPrograms.findIndex(p => p.id === programId);
    if (programIndex === -1) {
      throw new Error(`Program with ID ${programId} not found`);
    }

    // 3. Sanitize dates in updates before applying
    // Don't add day compensation when saving - we want to save the user's selected date
    const sanitizedUpdates = {
      ...updates,
      program_data: updates.program_data ? sanitizeProgramDataDates(updates.program_data, false) : updates.program_data
    };
    
    // 4. Update the program
    const updatedProgram: TrackedProgram = {
      ...currentPrograms[programIndex],
      ...sanitizedUpdates,
      updated_at: getCurrentTimestamp()
    };
    
    // Ensure program_data dates are also sanitized (no day compensation when saving)
    if (updatedProgram.program_data) {
      updatedProgram.program_data = sanitizeProgramDataDates(updatedProgram.program_data, false);
    }

    // 4. Replace in array
    const updatedPrograms = [...currentPrograms];
    updatedPrograms[programIndex] = updatedProgram;

    // 5. Save to database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        tracked_programs: updatedPrograms,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to update tracked program: ${updateError.message}`);
    }

    return updatedProgram;
    
  } catch (error) {
    console.error('Error in updateTrackedProgram:', error);
    throw error;
  }
}

/**
 * Delete a tracked program
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} programId - The UUID of the program to delete
 * @returns {Promise<void>}
 * @throws {Error} If deletion fails
 */
export async function deleteTrackedProgram(
  userId: string, 
  programId: string
): Promise<void> {
  try {
    // 1. Fetch current programs
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('tracked_programs')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    // Handle case where profile doesn't exist yet
    if (!profile) {
      throw new Error(`Profile not found for user ${userId}`);
    }

    const currentPrograms = Array.isArray(profile?.tracked_programs) 
      ? profile.tracked_programs 
      : [];

    // 2. Check if program exists
    const programExists = currentPrograms.some(p => p.id === programId);
    if (!programExists) {
      throw new Error(`Program with ID ${programId} not found`);
    }

    // 3. Remove from array
    const updatedPrograms = currentPrograms.filter(p => p.id !== programId);

    // 4. Save to database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        tracked_programs: updatedPrograms,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to delete tracked program: ${updateError.message}`);
    }
    
  } catch (error) {
    console.error('Error in deleteTrackedProgram:', error);
    throw error;
  }
}

/**
 * Count tracked programs with optional filters
 * Used for planning progress calculations
 * 
 * @param {string} userId - The UUID of the user
 * @param {Object} filters - Optional filters
 * @param {string} filters.status - Filter by status
 * @param {number} filters.year - Filter by year
 * @returns {Promise<number>} Count of programs matching filters
 */
export async function countTrackedPrograms(
  userId: string,
  filters?: { status?: string; year?: number }
): Promise<number> {
  try {
    let programs = await getTrackedPrograms(userId);

    // Apply filters
    if (filters) {
      if (filters.status) {
        programs = programs.filter(p => p.status === filters.status);
      }
      if (filters.year) {
        programs = programs.filter(p => p.year_tracked === filters.year);
      }
    }

    return programs.length;
  } catch (error) {
    console.error('Error in countTrackedPrograms:', error);
    throw error;
  }
}

/**
 * Get programs for specific children
 * 
 * @param {string} userId - The UUID of the user
 * @param {string[]} childNames - Array of child names to filter by
 * @returns {Promise<TrackedProgram[]>} Programs that include any of the specified children
 */
export async function getTrackedProgramsByChildren(
  userId: string,
  childNames: string[]
): Promise<TrackedProgram[]> {
  try {
    const programs = await getTrackedPrograms(userId);
    return programs.filter(program => 
      childNames.some(name => program.child_names.includes(name))
    );
  } catch (error) {
    console.error('Error in getTrackedProgramsByChildren:', error);
    throw error;
  }
}

/**
 * Bulk update multiple programs at once
 * More efficient than updating one at a time
 * 
 * @param {string} userId - The UUID of the user
 * @param {Array} updates - Array of {programId, updates} objects
 * @returns {Promise<void>}
 * @throws {Error} If any update fails
 */
export async function bulkUpdateTrackedPrograms(
  userId: string,
  updates: Array<{ programId: string; updates: Partial<TrackedProgram> }>
): Promise<void> {
  try {
    // 1. Fetch current programs
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('tracked_programs')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    // Handle case where profile doesn't exist yet
    if (!profile) {
      throw new Error(`Profile not found for user ${userId}`);
    }

    const currentPrograms = Array.isArray(profile?.tracked_programs) 
      ? profile.tracked_programs 
      : [];

    // 2. Apply all updates
    const updatedPrograms = [...currentPrograms];
    for (const updateItem of updates) {
      const programIndex = updatedPrograms.findIndex(p => p.id === updateItem.programId);
      if (programIndex !== -1) {
        updatedPrograms[programIndex] = {
          ...updatedPrograms[programIndex],
          ...updateItem.updates,
          updated_at: getCurrentTimestamp()
        };
      }
    }

    // 3. Save to database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        tracked_programs: updatedPrograms,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to bulk update tracked programs: ${updateError.message}`);
    }
    
  } catch (error) {
    console.error('Error in bulkUpdateTrackedPrograms:', error);
    throw error;
  }
}